import React, { useEffect, useState } from "react";
import { Table, Spin, Switch } from "antd";
import { Helmet as HelmetAsync } from "react-helmet-async";
import styled from "styled-components";
import { useIntl } from "react-intl";
import clsx from "clsx";
import {
  assetsSelector,
  genQAListSelector
} from "../../redux/selectors";
import {
  useAppDispatch,
  useAppSelector
} from "../../redux/store";
import {
  getGenQAList
} from "../../redux/actions";
import StyledText from "../../components/StyledText";

const Questions = ({ className }) => {
  const intl = useIntl();
  const baseClassName = clsx("insights", className);
  const assetID = sessionStorage.getItem('assetID') ?? null;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const assets = useAppSelector(assetsSelector);
  const contentListData = useAppSelector(genQAListSelector);
  const firstAsset = Array.isArray(assets) && assets.length > 0 ? assets[0] : null;
  const { isLocalWithin, projectID } = firstAsset ?? {};
  const [isLocal, setIsLocal] = useState("N");
  const [isGrouped, setIsGrouped] = useState("N");
  const [sortOrder, setSortOrder] = useState("asc");
  const [contentList, setContentList] = useState([]);

  useEffect(() => {
    if (assetID && projectID) {
      setIsLoading(true);
      dispatch(getGenQAList({
        assetID,
        projectID,
        sort: sortOrder,
        group: isGrouped,
        isLocal: isLocal,
      }));
    }
  }, [isLocal, isGrouped, assetID, projectID, sortOrder, dispatch]);

  useEffect(() => {
    if (contentListData !== undefined) {
      setIsLoading(true);
      const convertedList = convertToArray(contentListData);
      setContentList(convertedList);
      setIsLoading(false);
    }
  }, [contentListData]);

  function convertToArray(jsonData) {
    if (!jsonData || typeof jsonData !== 'object') {
      return [];
    }
    return Object.values(jsonData).flat();
  }

  const getOpportunityColumnData = (list) => {
    if (!Array.isArray(list) || list.length === 0) {
      return [];
    }

    if (isGrouped === "Y") {
      const groupedData = list.reduce((acc, item) => {
        if (!item || !item.visitorID || !item.questionTime) {
          return acc;
        }
        const { visitorID, questionTime } = item;
        if (!acc[visitorID] || new Date(questionTime) > new Date(acc[visitorID].questionTime)) {
          acc[visitorID] = item;
        }
        return acc;
      }, {});

      return Object.values(groupedData).map((item, idx) => {
        if (!item) {
          return null;
        }
        const { visitorID, question, answer, questionTime } = item;
        return {
          key: idx,
          visitorID: visitorID ?? '',
          question: question ?? '',
          answer: answer ?? '',
          questionTime: questionTime ?? '',
        };
      }).filter(item => item !== null);
    }
    
    return list.map((item, idx) => {
      if (!item) {
        return null;
      }
      const { visitorID, question, answer, questionTime } = item;
      return {
        key: idx,
        visitorID: visitorID ?? '',
        question: question ?? '',
        answer: answer ?? '',
        questionTime: questionTime ?? '',
      };
    }).filter(item => item !== null);
  };

  const opportunityColumns = [
    {
      title: <StyledText as="p" variant="H3" className="status">
        {intl.formatMessage({ id: 'visitor_id' })}
      </StyledText>,
      dataIndex: 'visitorID',
      key: 'visitorID',
      fixed: 'left',
    },
    ...(isGrouped === "N" ? [
      {
        title: <StyledText as="p" variant="H3" className="status">
          {intl.formatMessage({ id: 'question' })}
        </StyledText>,
        dataIndex: 'question',
        key: 'question',
      },
      {
        title: <StyledText as="p" variant="H3" className="status">
          {intl.formatMessage({ id: 'answer' })}
        </StyledText>,
        dataIndex: 'answer',
        key: 'answer',
      },
    ] : []),
    {
      title: <StyledText as="p" variant="H3" className="status">
        {intl.formatMessage({ id: 'question_time' })}
      </StyledText>,
      dataIndex: 'questionTime',
      key: 'questionTime',
      sorter: (a, b) => {
        const dateA = a?.questionTime ? new Date(a.questionTime) : null;
        const dateB = b?.questionTime ? new Date(b.questionTime) : null;
        if (!dateA || !dateB || isNaN(dateA) || isNaN(dateB)) return 0;
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      },
      onHeaderCell: () => ({
        onClick: () => {
          setSortOrder(prev => prev === "asc" ? "desc" : "asc");
          setIsLoading(true);
        },
      }),
    },
  ];

  const handleLocalSwitchChange = (checked) => {
    setIsLoading(true);
    setIsLocal(checked ? "Y" : "N");
  };

  const handleGroupSwitchChange = (checked) => {
    setIsLoading(true);
    setIsGrouped(checked ? "Y" : "N");
  };

  const expandedRowRender = (record) => {
    if (!record?.visitorID) {
      return null;
    }
    const filteredData = contentList.filter(item => item?.visitorID === record.visitorID) ?? [];
    return (
      <Table
        columns={[
          { 
            title: 'Question', 
            dataIndex: 'question', 
            key: 'question',
            render: (text) => text ?? ''
          },
          { 
            title: 'Answer', 
            dataIndex: 'answer', 
            key: 'answer',
            render: (text) => text ?? ''
          },
          { 
            title: 'Question Time', 
            dataIndex: 'questionTime', 
            key: 'questionTime',
            render: (text) => text ?? ''
          },
        ]}
        dataSource={filteredData}
        pagination={false}
      />
    );
  };

  return (
    <div className={baseClassName}>
      <HelmetAsync>
        <title>Insights Page</title>
      </HelmetAsync>
      <div className="toggleParentWrapper">
        <div className="toggleWrapper">
          <Switch onChange={handleLocalSwitchChange} />
          <StyledText as="p" variant="B3" className="status">
            {intl.formatMessage({ id: 'local_only' })}
          </StyledText>
          {isLocalWithin && (
            <div className="toggleWrapper">
              <StyledText as="p" variant="B3" className="status">
                {intl.formatMessage({ id: 'within_km' }).replace('value', isLocalWithin)}
              </StyledText>
            </div>
          )}
        </div>
        <div className="toggleWrapper">
          <Switch onChange={handleGroupSwitchChange} />
          <StyledText as="p" variant="B3" className="status">
            {intl.formatMessage({ id: 'group_by_visitor' })}
          </StyledText>
        </div>
      </div>
      <div className="insightsGraphContainer">
        <Table
          columns={opportunityColumns}
          dataSource={getOpportunityColumnData(contentList)}
          loading={isLoading}
          expandable={isGrouped === "Y" ? { expandedRowRender } : undefined}
          pagination={false}
        />
      </div>
    </div>
  );
};

const StyledQuestions = styled(Questions)`
  &.insights {
    background: ${(props) => props.theme.backgroundCanvas};
    .title {
      margin-bottom: 15px;
    }
    .toggleParentWrapper {
      display: flex;
      flex-direction: column;
      width: max-content;
      gap: 20px;
      .toggleWrapper {
        display: flex;
        gap: 10px;
      }
    }
    .insightsGraphContainer {
      margin: 30px 0;
    }
  }
`;

export default StyledQuestions;